import { SUPPORTED_METRICS } from '../config/sensorMetrics';
import { convertValue, getDisplayUnit, getUnitOptions } from './unitConversions';
import { formatInStationTimezone } from './timezoneDisplay';

export interface TimeSeriesData {
  timestamp: string;
  [key: string]: any;
}

export interface StationData {
  id: string;
  name: string;
  description?: string;
  timeseries: TimeSeriesData[];
  period_hours: number;
  data_points: number;
}

/**
 * Convert timeseries data to CSV format.
 * Applies unit conversions if unitPreferences are provided.
 */
export const convertToCSV = (
  stationData: StationData,
  unitPreferences?: Record<string, string> | null,
  stationTimezone?: string | null
): string => {
  if (stationData.timeseries.length === 0) {
    return '# No data available\n';
  }

  // Collect all unique metric keys
  const allKeys = new Set<string>();
  stationData.timeseries.forEach(row => {
    Object.keys(row).forEach(key => {
      if (key !== 'timestamp' && key !== 'time' && key !== 'timestampMs') {
        allKeys.add(key);
      }
    });
  });

  const metricKeys = Array.from(allKeys).sort();

  // Build header mapping from SUPPORTED_METRICS + unit preferences
  const headerMapping: Record<string, string> = {};
  SUPPORTED_METRICS.forEach(m => {
    const displayUnit = unitPreferences ? getDisplayUnit(m.internal, unitPreferences) : m.unit;
    const name = m.userFriendly.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
    headerMapping[m.internal] = `${name} (${displayUnit})`;
  });

  // Build headers
  const headers = ['Timestamp', ...metricKeys.map(key => headerMapping[key] || key)];

  // Create CSV with BOM for Excel UTF-8 compatibility
  let csv = '\uFEFF' + headers.join(',') + '\n';

  // Add data rows with unit conversion
  stationData.timeseries.forEach(row => {
    const csvRow = [
      stationTimezone ? formatInStationTimezone(row.timestamp, stationTimezone) : (row.timestamp || ''),
      ...metricKeys.map(key => {
        const value = row[key];
        if (value === null || value === undefined) return '';
        // Apply unit conversion if preferences exist
        if (unitPreferences && unitPreferences[key] && typeof value === 'number') {
          return convertValue(value, key, unitPreferences[key]).toFixed(4);
        }
        return value.toString();
      })
    ];
    csv += csvRow.join(',') + '\n';
  });

  return csv;
};

/**
 * Download CSV data as a file
 */
export const downloadCSV = (csvData: string, filename: string) => {
  const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Generate filename for CSV export
 */
export const generateCSVFilename = (stationName: string): string => {
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-').split('T')[0];
  const sanitizedName = stationName.replace(/[^a-zA-Z0-9_-]/g, '_');
  return `station_${sanitizedName}_${timestamp}.csv`;
};
